const FAILURE_THRESHOLD = 2
const OPEN_MS = 15 * 60_000

interface CircuitState {
    failures: number
    lastError: string
    openUntil?: number
    // Set when the failure that opened the circuit was kind=config (wrong
    // IMEI, integration disabled) — those never self-heal by waiting, so the
    // circuit stays open forever until a manual retry (recordSuccess).
    kind?: string
}

export interface CameraLiveCircuitOpenState {
    lastError: string
    // undefined for a permanent (kind=config) circuit: nothing is scheduled
    // to reopen it, only recordSuccess does.
    retryAt?: number
    kind?: string
}

const circuits = new Map<string, CircuitState>()

function getOpenState(vehicleId: string): CameraLiveCircuitOpenState | null {
    const state = circuits.get(vehicleId)
    if (!state) return null
    if (state.kind === 'config') {
        return { lastError: state.lastError, kind: state.kind }
    }
    if (!state.openUntil) return null
    if (Date.now() >= state.openUntil) {
        circuits.delete(vehicleId)
        return null
    }
    return { lastError: state.lastError, retryAt: state.openUntil }
}

export const cameraLiveCircuit = {
    shouldSkip(vehicleId: string): boolean {
        return getOpenState(vehicleId) !== null
    },

    getOpenState,

    // kind: pass 'config' for a configuration mistake — opens on the FIRST
    // failure and never auto-reopens, unlike a transient failure (2 failures,
    // 15-minute cooldown).
    recordFailure(vehicleId: string, error: string, kind?: string): void {
        if (kind === 'config') {
            const current = circuits.get(vehicleId)
            circuits.set(vehicleId, {
                failures: (current?.failures ?? 0) + 1,
                lastError: error,
                kind,
            })
            return
        }
        const current = circuits.get(vehicleId)
        const failures = (current?.failures ?? 0) + 1
        circuits.set(vehicleId, {
            failures,
            lastError: error,
            openUntil:
                failures >= FAILURE_THRESHOLD ? Date.now() + OPEN_MS : undefined,
        })
    },

    recordSuccess(vehicleId: string): void {
        circuits.delete(vehicleId)
    },
}
